import { LitElement, html, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { Task } from "@lit/task";
import { fetchApi, ApiError, types } from "../api/index.js";
import { shortDate, shortDigest, shortCommit, commitUrl, externalLinkIcon, apiLink } from "../utils/format.js";
import { uniqueStreams, variantOf, streamOf } from "../utils/canonical.js";
import "../components/loading-spinner.js";
import "../components/error-message.js";
import "../components/copy-button.js";

@customElement("history-tab")
export class HistoryTab extends LitElement {
  @property() imageName = "";
  @property() canonical = "";
  @property({ type: Array }) canonicals: string[] = [];
  @property({ type: Array }) tags: types.Tag[] = [];
  @state() private _stream = "";
  @state() private _variant = "";
  @state() private _expandedDigest = "";
  @state() private _drillTab: "details" | "sbom" = "details";
  @state() private _drillData: types.DetailsResponse | types.SbomResponse | null = null;
  @state() private _drillLoading = false;

  createRenderRoot() {
    return this;
  }

  /** Effective stream: explicit selection or derived from current canonical. */
  private get _activeStream(): string {
    return this._stream || streamOf(this.canonical, this.tags);
  }

  /** Effective variant: explicit selection or derived from current canonical. */
  private get _activeVariant(): string {
    return this._variant || variantOf(this.canonical, this.tags);
  }

  private get _streams(): string[] {
    return uniqueStreams(this.tags);
  }

  /** Unique variant names from the tags list. */
  private get _variants(): string[] {
    const seen = new Set<string>();
    for (const t of this.tags) seen.add(t.variant);
    return [...seen];
  }

  private _task = new Task(this, {
    args: () => [this.imageName, this._activeStream, this._activeVariant] as const,
    task: ([name, stream, variant]) =>
      stream && variant ? fetchApi<types.HistoryResponse>(`/v1/images/${name}/history/${stream}/${variant}`) : Promise.reject("No stream/variant"),
  });

  private async _toggleRelease(digest: string) {
    if (this._expandedDigest === digest) {
      this._expandedDigest = "";
      this._drillData = null;
      return;
    }
    this._expandedDigest = digest;
    this._drillTab = "details";
    await this._fetchDrill(digest, "details");
  }

  private async _fetchDrill(digest: string, tab: "details" | "sbom") {
    this._drillLoading = true;
    this._drillData = null;
    const hex = digest.replace("sha256:", "");
    try {
      if (tab === "details") {
        this._drillData = await fetchApi<types.DetailsResponse>(
          `/v1/images/${this.imageName}/releases/details/${hex}`,
        );
      } else {
        this._drillData = await fetchApi<types.SbomResponse>(
          `/v1/images/${this.imageName}/releases/sbom/${hex}`,
        );
      }
    } catch {
      this._drillData = null;
    }
    this._drillLoading = false;
  }

  private async _setDrillTab(tab: "details" | "sbom") {
    this._drillTab = tab;
    await this._fetchDrill(this._expandedDigest, tab);
  }

  private _renderDrill() {
    if (this._drillLoading) return html`<loading-spinner></loading-spinner>`;
    if (!this._drillData) return html`<p class="text-gray-500 text-xs">No data available.</p>`;

    if (this._drillTab === "details") {
      const detailsMap = this._drillData as types.DetailsResponse;
      const firstArch = Object.keys(detailsMap)[0];
      if (!firstArch) return html`<p class="text-gray-500 text-xs">No details available.</p>`;
      const data = detailsMap[firstArch];
      return html`
        <div class="text-xs space-y-2">
          <div><span class="text-gray-500">User:</span> <span class="text-gray-200 font-mono">${data.user}</span></div>
          <div>
            <span class="text-gray-500">Entrypoint:</span>
            <span class="text-gray-200 font-mono">${JSON.stringify(data.entrypoint)}</span>
          </div>
          <div>
            <span class="text-gray-500">CMD:</span>
            <span class="text-gray-200 font-mono">${JSON.stringify(data.cmd)}</span>
          </div>
          <div>
            <span class="text-gray-500">Env vars:</span>
            <span class="text-gray-200">${Object.keys(data.env).length}</span>
          </div>
          <div>
            <span class="text-gray-500">Labels:</span>
            <span class="text-gray-200">${Object.keys(data.labels).length}</span>
          </div>
          ${data.source_commit
            ? html`
                <div>
                  <span class="text-gray-500">Commit:</span>
                  ${data.source_repo
                    ? html`<a href=${commitUrl(data.source_repo, data.source_commit)} target="_blank"
                        class="text-gray-200 font-mono hover:text-gray-100 transition-colors"
                        >${shortCommit(data.source_commit)}${externalLinkIcon}</a>`
                    : html`<span class="text-gray-200 font-mono">${shortCommit(data.source_commit)}</span>`}
                </div>
              `
            : ""}
        </div>
      `;
    }
    const data = this._drillData as types.SbomResponse;
    const firstArch = Object.keys(data)[0];
    if (!firstArch) return html`<p class="text-gray-500 text-xs">No SBOM data available.</p>`;
    const arch = data[firstArch];
    const visible = arch.packages.filter((p) => p.category !== "source");
    const count = (arch.runtime_count ?? 0) + (arch.build_count ?? 0);
    return html`
      <div class="text-xs">
        <p class="text-gray-400 mb-2">${count} packages (${firstArch})</p>
        <div class="max-h-48 overflow-y-auto">
          ${visible.slice(0, 50).map(
            (p) => html`
              <div class="flex gap-3 py-0.5 border-b border-hb-border/30">
                <span class="text-gray-200 font-mono w-48 shrink-0">${p.name}</span>
                <span class="text-gray-500 font-mono">${p.evr}</span>
              </div>
            `,
          )}
          ${visible.length > 50 ? html`<p class="text-gray-500 py-1">... and ${visible.length - 50} more</p>` : ""}
        </div>
      </div>
    `;
  }

  private _onStreamChange(stream: string) {
    this._stream = stream;
    this._expandedDigest = "";
    this._drillData = null;
  }

  private _onVariantChange(e: Event) {
    const variant = (e.target as HTMLSelectElement).value;
    this._variant = variant;
    this._expandedDigest = "";
    this._drillData = null;
    // Propagate variant change upstream so other tabs update
    const match = this.tags.find((t) => t.variant === variant);
    if (match) this.dispatchEvent(new CustomEvent("canonical-change", { detail: match.canonical }));
  }

  private _filterButton(label: string, active: boolean, handler: () => void) {
    return html`
      <button
        @click=${handler}
        class="px-2 py-1 text-xs cursor-pointer transition-colors border -ml-px first:ml-0
               rounded-none first:rounded-l last:rounded-r
               ${active ? "bg-hb-accent text-hb-bg border-hb-accent relative z-10" : "bg-hb-surface text-gray-400 hover:text-gray-200 border-hb-border"}"
      >
        ${label}
      </button>
    `;
  }

  render() {
    const showStreamFilter = this._streams.length > 1;
    const showVariantFilter = this._variants.length > 1;
    const activeStream = this._activeStream;
    const activeVariant = this._activeVariant;

    return html`
      <div class="mb-4 flex flex-wrap items-center gap-3">
        ${showStreamFilter
          ? html`
              <div class="flex flex-wrap">
                ${this._streams.map((s) =>
                  this._filterButton(s, activeStream === s, () => this._onStreamChange(s)),
                )}
              </div>
            `
          : ""}
        ${showVariantFilter
          ? html`
              <div>
                <label class="text-gray-500 text-sm mr-1">Variant:</label>
                <select
                  .value=${activeVariant}
                  @change=${this._onVariantChange}
                  class="bg-hb-surface border border-hb-border rounded px-2 py-1 text-sm text-gray-200"
                >
                  ${this._variants.map((v) => html`<option value=${v} ?selected=${v === activeVariant}>${v}</option>`)}
                </select>
              </div>
            `
          : ""}
        <div class="ml-auto">
          ${this.imageName ? apiLink(`/v1/images/${this.imageName}/history/${activeStream}/${activeVariant}`) : ""}
        </div>
      </div>

      ${this._task.render({
        pending: () => html`<loading-spinner></loading-spinner>`,
        error: (e) =>
          e instanceof ApiError && e.code === "not_found"
            ? html`<div class="rounded-lg border border-hb-border bg-hb-surface/50 p-4 text-gray-400 text-sm">
                No release history available yet. History will appear here after the next data sync.
              </div>`
            : html`<error-message .message=${String(e)}></error-message>`,
        complete: (data) => {
          const releases = data.releases;
          return html`
            <div class="border border-hb-border rounded-lg overflow-hidden">
              <table class="w-full text-left text-xs">
                <thead class="bg-hb-surface text-gray-400">
                  <tr>
                    <th class="py-2 px-3">Date</th>
                    <th class="py-2 px-3">Digest</th>
                    <th class="py-2 px-3 hidden md:table-cell">Commit</th>
                    <th class="py-2 px-3 hidden md:table-cell">Archs</th>
                  </tr>
                </thead>
                <tbody>
                  ${releases.map(
                    (rel) => html`
                      <tr
                        @click=${() => this._toggleRelease(rel.digest)}
                        class="border-b border-hb-border hover:bg-hb-surface/50 cursor-pointer transition-colors"
                      >
                        <td class="py-2 px-3 text-gray-200">${shortDate(rel.oldest_created)}</td>
                        <td class="py-2 px-3 text-gray-400 font-mono">
                          <span title=${rel.digest}>${shortDigest(rel.digest)}</span>
                        </td>
                        <td class="py-2 px-3 text-gray-400 font-mono hidden md:table-cell">
                          ${rel.source_commit && rel.source_repo
                            ? html`<a href=${commitUrl(rel.source_repo, rel.source_commit)} target="_blank"
                                class="hover:text-gray-200 transition-colors"
                                @click=${(e: Event) => e.stopPropagation()}
                                title=${rel.source_commit}
                                >${shortCommit(rel.source_commit)}${externalLinkIcon}</a>`
                            : rel.source_commit
                              ? html`<span title=${rel.source_commit}>${shortCommit(rel.source_commit)}</span>`
                              : html`<span class="text-gray-600">\u2014</span>`}
                        </td>
                        <td class="py-2 px-3 text-gray-500 hidden md:table-cell">${rel.architectures.join(", ")}</td>
                      </tr>
                      ${this._expandedDigest === rel.digest
                        ? html`
                            <tr>
                              <td colspan="4" class="p-4 bg-hb-bg/50">
                                <div class="flex gap-2 mb-3">
                                  <button
                                    @click=${(e: Event) => {
                                      e.stopPropagation();
                                      this._setDrillTab("details");
                                    }}
                                    class="px-2 py-1 text-xs rounded cursor-pointer
                                           ${this._drillTab === "details" ? "bg-hb-accent text-hb-bg" : "bg-hb-surface text-gray-400 border border-hb-border"}"
                                  >
                                    Details
                                  </button>
                                  <button
                                    @click=${(e: Event) => {
                                      e.stopPropagation();
                                      this._setDrillTab("sbom");
                                    }}
                                    class="px-2 py-1 text-xs rounded cursor-pointer
                                           ${this._drillTab === "sbom" ? "bg-hb-accent text-hb-bg" : "bg-hb-surface text-gray-400 border border-hb-border"}"
                                  >
                                    SBOM
                                  </button>
                                </div>
                                ${this._renderDrill()}
                              </td>
                            </tr>
                          `
                        : nothing}
                    `,
                  )}
                </tbody>
              </table>
            </div>
          `;
        },
      })}
    `;
  }
}
